import { SegmentedButton } from "@components/button/SegmentedButton";
import {
    getDrillDownNavParams,
    getNavLinkComponent,
    getTableDrillDownLink
} from "@components/drillDown/DrillDown.utils";
import ErrorDetail from "@components/errorDetail/ErrorDetail";
import { IRow } from "@components/table";
import TableWithAutoSizedColumns from "@components/table/TableWithAutoSizedColumns";
import { getRouteByDocumentType } from "@odata/EntityTypes";
import { BackgroundJobEntity, InvoiceReceivedEntity } from "@odata/GeneratedEntityTypes";
import { BackgroundJobTypeCode } from "@odata/GeneratedEnums";
import {
    IBackgroundJobCustomData,
    IBackgroundJobParsedResults
} from "@pages/backgroundJob/BackgroundJob.utils";
import {
    BackgroundJobGroupButtons,
    BackgroundJobResultsWrapper,
    BackgroundJobTableWrapper, GeneralErrorInfo, GeneralErrorWrapper
} from "@pages/backgroundJob/BackgrounJob.styles";
import { isDefined, isNotDefined } from "@utils/general";
import i18next from "i18next";
import React from 'react';

import { NEW_ITEM_DETAIL } from "../../constants";
import { QueryParam, Status } from "../../enums";
import { TRecordAny } from "../../global.types";
import { FormStorage } from "../../views/formView/FormStorage";

enum Groups {
    Error = "Error",
    Detail = "Detail"
}

interface IProps {
    storage: FormStorage;
}

const VIRTUALIZATION_LIMIT = 50;

export class BackgroundJobResults extends React.Component<IProps> {
    _selectedGroup = Groups.Error;

    get Results(): IBackgroundJobParsedResults {
        return (this.props.storage as FormStorage<{}, IBackgroundJobCustomData>).getCustomData()?.results;
    }

    get IsDraft() {
        const type = this.props.storage.data.entity[BackgroundJobEntity.Type]?.Code;
        return BackgroundJobTypeCode.BulkChangeOfDrafts === type || type === BackgroundJobTypeCode.PostMultipleDrafts;
    }

    getRouteCustomQueryString = () => {
        return this.IsDraft ? `${QueryParam.Drafts}=true` : ``;
    }

    getColumns = () => {
        const _isError = this._selectedGroup === Groups.Error;
        return [
            {
                id: _isError ? "count" : "numberOurs",
                label: i18next.t(_isError ? "BackgroundJobs:Form.Count" : "BackgroundJobs:Form.NumberOurs")
            },
            {
                id: "errorCode",
                label: i18next.t("BackgroundJobs:Form.Error")
            },
            ...(!_isError ? [{
                id: "detail",
                label: i18next.t("BackgroundJobs:Form.Detail")
            }] : [])
        ];
    };

    createGroupRows = (): IRow[] => {
        const grouppedErrors = this.Results?.errors;
        const route = getRouteByDocumentType(this.Results.documentTypeCode);

        return Object.entries(grouppedErrors)?.map(([errorCode, errorObject], index) => ({
            id: index + 1,
            values: {
                count: getTableDrillDownLink(errorObject?.items.length, {
                    route,
                    customQueryString: this.getRouteCustomQueryString(),
                    filters: this.createNumberOursFilter(errorObject?.items),
                    context: this.props.storage.context,
                    storage: this.props.storage
                }),
                errorCode: i18next.t(`Error:${errorCode}`,
                    Object.fromEntries(Object.keys(errorObject.parameters ?? {}).map((key) => [key, '-']))) as string
            }
        }));
    };

    createNumberOursFilter = (items: TRecordAny[]) => {
        const numberOurs = items
            .filter(({ NumberOurs }) => isDefined(NumberOurs))
            .map(({ NumberOurs }) => NumberOurs);

        const ids = items
            .filter(({ NumberOurs }) => isNotDefined(NumberOurs))
            .map(({ ItemId }) => ItemId);

        if (numberOurs.length === 0 && ids.length === 0) {
            return null;
        }

        return {
            ...(numberOurs.length > 0 && { [InvoiceReceivedEntity.NumberOurs]: numberOurs }),
            ...(ids.length > 0 && { Id: ids })
        };
    }

    createDetailRows = (): IRow[] => {
        const route = getRouteByDocumentType(this.Results.documentTypeCode);

        const _createRows = (rows: TRecordAny[]) => {
            return rows?.map((row: TRecordAny, index: number) => {
                const message = row.Exception?.ValidationMessages?.[0];
                const errorCode = message?.ErrorCode;
                const hasError = !!errorCode;
                return {
                    id: index + 1,
                    values: {
                        numberOurs: getTableDrillDownLink(row.NumberOurs ?? `${i18next.t("Components:Table.Drafts")} (${row.ItemId})`, {
                            route: this.IsDraft ? `${route}/${NEW_ITEM_DETAIL}` : `${route}/${row.ItemId}`,
                            customQueryString: this.IsDraft ? `${QueryParam.DraftId}=${row.ItemId}&${QueryParam.Drafts}=true` : ``,
                            context: this.props.storage.context,
                            storage: this.props.storage,
                        }),
                        errorCode: hasError ? i18next.t(`Error:${errorCode}`, { ...message?.MessageParameters }) as string : null,
                        detail: hasError ? {
                            tooltip: i18next.t("BackgroundJobs:Form.Detail"),
                            value: (
                                <ErrorDetail data={row.Exception} status={Status.Error}/>
                            )
                        } : null
                    }
                };
            });
        };

        const rows = [];
        rows.push(..._createRows(this.Results.failed));

        return rows;
    };

    handleChangeGroup = (group: string) => {
        this._selectedGroup = group as Groups;
        this.props.storage.refresh();
    }

    renderGeneralError = () => {
        const msg = this.Results.generalError.ValidationMessages?.[0];
        const errorCode = msg?.ErrorCode;
        const title = i18next.exists(`Error:${errorCode}`) ? i18next.t(`Error:${errorCode}`, msg?.MessageParameters) : i18next.t(`BackgroundJobs:Form.GeneralError`);

        return (
            <GeneralErrorWrapper>
                {title}
                {this.Results.generalError.InnerException &&
                    <GeneralErrorInfo>
                        <ErrorDetail data={this.Results.generalError.InnerException} status={Status.Error}/>
                    </GeneralErrorInfo>
                }
            </GeneralErrorWrapper>
        );
    }

    render() {
        const storage = this.props.storage;

        if (!this.Results) {
            return null;
        }

        if (this.Results.generalError) {
            return this.renderGeneralError();
        }

        const errors = this.Results?.failed?.length || 0;
        const rows = this._selectedGroup === Groups.Error ? this.createGroupRows() : this.createDetailRows();
        const useVirtualization = rows?.length > VIRTUALIZATION_LIMIT;

        const route = getRouteByDocumentType(this.Results.documentTypeCode);
        const items = [
            ...(this.Results?.changed ?? []),
            ...(this.Results?.skipped ?? [])
        ];

        const successText = i18next.t("BackgroundJobs:Form.SuccessLink", {
            count: items.length
        });

        const type = storage.data.entity[BackgroundJobEntity.Type]?.Code;
        const successLink = getNavLinkComponent(successText, {
            route,
            // drafts successfully posted are no longer among drafts so redirect them to classic URL
            customQueryString: type === BackgroundJobTypeCode.PostMultipleDrafts ? null : this.getRouteCustomQueryString(),
            context: storage.context,
            storage: storage,
            filters: this.createNumberOursFilter(items)
        }, getDrillDownNavParams);

        const hasError = this.Results?.failed?.length > 0;
        const isLoaded = this.props.storage.loaded && !this.props.storage.loading && !!this.props.storage.data.bindingContext;

        return (
            <BackgroundJobResultsWrapper>
                <div>
                    {i18next.t("BackgroundJobs:Form.Success")}{" "}
                    {items.length > 0 ? successLink : successText}.
                </div>
                <div> {i18next.t("BackgroundJobs:Form.ErrorCount", { count: errors })}.</div>
                {hasError &&
                    <>
                        <BackgroundJobGroupButtons>
                            <SegmentedButton
                                onChange={this.handleChangeGroup}
                                selectedButtonId={this._selectedGroup ?? "Error"}
                                def={[{
                                    id: Groups.Error,
                                    label: i18next.t("BackgroundJobs:Form.GroupError")
                                }, {
                                    id: Groups.Detail,
                                    label: i18next.t("BackgroundJobs:Form.GroupDetail")
                                }]}/>
                        </BackgroundJobGroupButtons>
                        {isLoaded &&
                            <BackgroundJobTableWrapper $useVirtualizations={useVirtualization}>
                                <TableWithAutoSizedColumns
                                    key={this.props.storage.data.bindingContext.getKey()}
                                    disableVirtualization={!useVirtualization}
                                    disableColumnShortening
                                    disableSort={true}
                                    columns={this.getColumns()}
                                    rows={rows}/>
                            </BackgroundJobTableWrapper>
                        }
                    </>
                }
            </BackgroundJobResultsWrapper>
        );
    }
}